import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { FileDashedIcon } from "@phosphor-icons/react/dist/ssr/FileDashed";
import { FileTextIcon } from "@phosphor-icons/react/dist/ssr/FileText";
import { FileXIcon } from "@phosphor-icons/react/dist/ssr/FileX";

import {
  COUPON_AMOUNT_TYPE,
  COUPON_TYPE,
  ProfileType,
  ScholarshipStatus,
  applicationIsDenied,
  applicationIsIncomplete,
  ownerStore,
} from "@up/data";
import { UPLink } from "@up/ui";

import { LineItem } from "../line-item";

import type { TContact, TCoupon, TProfile, TScholarshipStatus } from "@up/data";

function isScholarshipCoupon(coupon: TCoupon) {
  return (
    coupon.type === COUPON_TYPE.FULL_SCHOLARSHIP ||
    coupon.type === COUPON_TYPE.PARTIAL_SCHOLARSHIP
  );
}

function isExpired(coupon: TCoupon) {
  return coupon.expiration ? new Date(coupon.expiration) < new Date() : false;
}

/**
 * Selects the scholarship coupon to display: drops expired coupons, keeps only
 * scholarship types, and prefers the first `fullScholarship` over any partial.
 */
function selectScholarshipCoupon(coupons?: TCoupon[]) {
  const scholarships = (coupons ?? [])
    .filter((coupon) => !isExpired(coupon))
    .filter(isScholarshipCoupon);

  return (
    scholarships.find(
      (coupon) => coupon.type === COUPON_TYPE.FULL_SCHOLARSHIP,
    ) ?? scholarships[0]
  );
}

function formatScholarshipAmount(coupon: TCoupon) {
  if (coupon.amountType === COUPON_AMOUNT_TYPE.PERCENTAGE) {
    return `${coupon.amount}%`;
  }
  return `$${(coupon.amount / 100).toFixed(2)}`;
}

export function ScholarshipStatusView({
  authenticatedProfileType,
  targetContact,
  status,
}: {
  authenticatedProfileType?: TProfile["type"];
  targetContact?: TContact;
  status?: TScholarshipStatus;
}) {
  if (!authenticatedProfileType || !targetContact) {
    return null;
  }

  const APP_INCOMPLETE = applicationIsIncomplete(
    targetContact.application?.status,
  );

  const APP_DENIED = applicationIsDenied(targetContact.application?.status);

  if (authenticatedProfileType === ProfileType.Student && APP_INCOMPLETE) {
    return (
      <LineItem compact={true} Icon={FileDashedIcon}>
        <div>
          Scholarship Status: No request on file. Please complete the
          application first
        </div>
      </LineItem>
    );
  }

  if (authenticatedProfileType === ProfileType.Student && APP_DENIED) {
    return null;
  }

  if (authenticatedProfileType === ProfileType.Guardian && APP_DENIED) {
    return (
      <LineItem compact={true} Icon={FileDashedIcon}>
        <div>Scholarship: Students must be admitted in order to enroll</div>
      </LineItem>
    );
  }

  if (authenticatedProfileType === ProfileType.Guardian && APP_INCOMPLETE) {
    return (
      <LineItem compact={true} Icon={FileDashedIcon}>
        <div>
          Scholarship Status: No request on file. Please have your student
          complete the application
        </div>
      </LineItem>
    );
  }

  switch (status) {
    case ScholarshipStatus.PendingReview:
    case ScholarshipStatus.OnHoldCX:
    case ScholarshipStatus.OnHoldUser:
      return (
        <LineItem compact={true} Icon={FileTextIcon}>
          Scholarship Status: In Review
        </LineItem>
      );

    case ScholarshipStatus.Approved:
    case ScholarshipStatus.AdminCompleted: {
      const scholarship = selectScholarshipCoupon(targetContact.coupons);
      return (
        <LineItem compact={true} Icon={CheckCircleIcon}>
          Scholarship Status: Awarded
          {scholarship ? ` — ${formatScholarshipAmount(scholarship)}` : null}
        </LineItem>
      );
    }

    case ScholarshipStatus.Denied:
      return (
        <LineItem compact={true} Icon={FileXIcon}>
          Scholarship Status: Not Awarded
        </LineItem>
      );

    case undefined:
      return (
        <LineItem
          as={UPLink}
          compact={true}
          href="/scholarship"
          Icon={FileDashedIcon}
          onClick={() => {
            ownerStore.set({
              email: targetContact.profile.email,
              firstName: targetContact.profile.firstName,
              lastName: targetContact.profile.lastName,
              contactId: targetContact._id,
            });
          }}
        >
          <div>
            Scholarship Status: No request on file
            <div className="underline">Apply for a need-based scholarship</div>
          </div>
        </LineItem>
      );

    default:
      return null;
  }
}
